import React from 'react';
import { Stack, styled } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { SignUpForm, SignUpHeaderBar } from 'components';
import { ConfirmationDialog } from 'components/ConfirmationDialog/ConfirmationDialog';
import { SignUpHelpDialog } from 'components/SignUpHelpDialog/SignUpHelpDialog';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useSignup } from 'features/signUp/hooks/useSignup';
import { Alert } from 'components/Alert/Alert';
import { Error } from '@mui/icons-material';
import { FormProvider } from 'react-hook-form';
import { useSignupSectionNavigation } from 'features/signUp/hooks/useSignupSectionNaivgation';
import { ErrorDialog } from 'components/ErrorDialog/ErrorDialog';
import { AccessRestrictionDialog } from 'components/AccessRestrictionDialog/AccessRestrictionDialog';

const DesktopTabletStyledGrid = styled(Grid)(({ theme }) => ({
  overflow: 'auto',
  width: '100%',
  flex: 1,
}));

const DesktopTabletStyledGridItem = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '580px',
  width: '100%',
  backgroundColor: theme.palette.background.paper,
  margin: 'auto',
}));

export const SignUp = () => {
  const {
    formMethods,
    currentStep,
    isApiRequestInProgress,
    progressPercentage,
    userType,
    eligibilityCheckMethod,
    handleFormSubmit,
    showExitDialog,
    handleExitDialogClose,
    handleConfirmExit,
    isBackButtonDisabled,
    signUpFormData,
    isHelpDialogOpen,
    openHelpDialog,
    closeHelpDialog,
    isShowingAlertError,
    alertError,
    isShowingDialogError,
    dialogError,
    containerRef,
  } = useSignup();
  const { navigateToPreviousStep } = useSignupSectionNavigation();
  const { translation } = useTranslationContext();
  const isAccessRestricted = dialogError.type === 'AccessRestricted';
  const SignUpErrorDialog = isAccessRestricted ? AccessRestrictionDialog : ErrorDialog;

  return (
    <Stack alignItems="center" width="100%" height="100dvh">
      <SignUpHeaderBar
        stepValue={progressPercentage}
        onClickBackButton={isBackButtonDisabled ? undefined : navigateToPreviousStep}
        hasProgressBar
        backButtonDisabled={isBackButtonDisabled}
      />
      <DesktopTabletStyledGrid direction="column" container ref={containerRef}>
        <DesktopTabletStyledGridItem>
          <FormProvider {...formMethods}>
            <SignUpForm
              currentStep={currentStep}
              userType={userType}
              eligibilityCheckMethod={eligibilityCheckMethod}
              isApiRequestInProgress={isApiRequestInProgress}
              onSubmit={handleFormSubmit}
              openHelpDialog={openHelpDialog}
            />
          </FormProvider>
        </DesktopTabletStyledGridItem>
      </DesktopTabletStyledGrid>
      <>
        <SignUpHelpDialog
          isHelpDialogOpen={isHelpDialogOpen}
          onHelpDialogClose={closeHelpDialog}
          signUpFormData={signUpFormData}
        />
        <ConfirmationDialog
          open={showExitDialog}
          handleYes={handleConfirmExit}
          handleNo={handleExitDialogClose}
          title={translation('exit_sign_up_alert')}
          description={translation('restart_signup_alert_title')}
          yesButtonLabel={translation('general_yes')}
          noButtonLabel={translation('general_no')}
        />
        <Alert
          isPresented={isShowingAlertError}
          variant="filled"
          severity="error"
          message={alertError.message}
          icon={<Error />}
          anchorOrigin={{ vertical: alertError.position, horizontal: 'center' }}
          handleDismiss={alertError.handleDismiss}
          snackbarSx={{ position: 'fixed' }}
        />
        <SignUpErrorDialog
          open={isShowingDialogError}
          title={dialogError.title}
          body={dialogError.body}
          {...(!isAccessRestricted && {
            helpElement: dialogError.helpElement,
            secondaryCtaButton: dialogError.secondaryCtaButton,
          })}
          primaryCtaButton={dialogError.primaryCtaButton!}
          onClose={dialogError.onClose}
        />
      </>
    </Stack>
  );
};
